import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { InformationBoxComponent } from '@principle-theorem/ng-principle-shared';
import {
  AttachmentsComponent,
  DialogPresets,
  FileManagerService,
  NgDialogModule,
  NgSharedModule,
  PipesModule,
  TypedFormControl,
} from '@principle-theorem/ng-shared';
import {
  FundingRequest,
  isMohClaimFundingRequest,
  isMohPreApprovalFundingRequest,
  MohClaimFundingRequest,
  MohPreApprovalFundingRequest,
} from '@principle-theorem/principle-core';
import {
  FundingRequestStatus,
  IMohFormDisplay,
  isMohClaimFundingRequestData,
  MohFundingRequest,
} from '@principle-theorem/principle-core/interfaces';
import {
  asyncForEach,
  DocumentReference,
  Firestore,
  getError,
  IAttachment,
  snapshot,
  WithRef,
} from '@principle-theorem/shared';
import { decodeFromBase64DataUri } from 'pdf-lib';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { MohService } from '../../../moh/moh.service';
import { MohApprovalRequestAttachmentsDialogComponent } from '../moh-approval-request-attachments-dialog/moh-approval-request-attachments-dialog.component';
import { MohFormViewerComponent } from '../moh-form-viewer/moh-form-viewer.component';

@Component({
  selector: 'pr-moh-sign-form-dialog',
  imports: [
    AttachmentsComponent,
    InformationBoxComponent,
    MohFormViewerComponent,
    NgDialogModule,
    NgSharedModule,
    PipesModule,
  ],
  templateUrl: './moh-sign-form-dialog.component.html',
  styleUrl: './moh-sign-form-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MohService],
})
export class MohSignFormDialogComponent {
  private _dialogRef = inject(
    MatDialogRef<
      MohSignFormDialogComponent,
      DocumentReference<MohFundingRequest>
    >
  );
  private _fileManager = inject(FileManagerService);
  private _dialog = inject(MatDialog);
  mohService = inject(MohService);

  preApprovalAttachments$: Observable<WithRef<IAttachment>[]>;

  fundingRequest = inject<WithRef<MohFundingRequest>>(MAT_DIALOG_DATA);
  signatureCtrl = new TypedFormControl<string>('', Validators.required);
  loading = signal(false);
  forms = this._getForms();
  attachments = toSignal(FundingRequest.attachments$(this.fundingRequest));
  storagePath = FundingRequest.storagePath(this.fundingRequest);

  constructor() {
    const preApprovalRequest$ = of(this.fundingRequest).pipe(
      switchMap((fundingRequest) =>
        isMohClaimFundingRequestData(fundingRequest.data) &&
        fundingRequest.data.approvalRequestRef
          ? Firestore.getDoc(fundingRequest.data.approvalRequestRef)
          : of(undefined)
      )
    );

    this.preApprovalAttachments$ = preApprovalRequest$.pipe(
      switchMap((request) =>
        request ? FundingRequest.attachments$(request) : of([])
      )
    );
  }

  async submit(): Promise<void> {
    this.loading.set(true);

    try {
      const signature = this.signatureCtrl.value;
      if (!signature) {
        return;
      }
      await this._uploadSignature(signature);
      this._dialogRef.close(this.fundingRequest.ref);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(getError(error));
      this._dialogRef.close();
    } finally {
      this.loading.set(false);
    }
  }

  async addAttachmentsFromApprovalRequest(): Promise<void> {
    const preApprovalAttachments = await snapshot(this.preApprovalAttachments$);
    const attachments = await this._dialog
      .open<
        MohApprovalRequestAttachmentsDialogComponent,
        WithRef<IAttachment>[],
        WithRef<IAttachment>[]
      >(
        MohApprovalRequestAttachmentsDialogComponent,
        DialogPresets.medium({ data: preApprovalAttachments })
      )
      .afterClosed()
      .toPromise();
    if (!attachments) {
      return;
    }

    await asyncForEach(attachments, (attachment) =>
      this.addAttachment(attachment)
    );
  }

  async addAttachment(attachment: IAttachment): Promise<void> {
    await FundingRequest.addAttachment(
      { ref: this.fundingRequest.ref },
      attachment
    );
  }

  async removeAttachment(attachment: WithRef<IAttachment>): Promise<void> {
    await FundingRequest.removeAttachment(attachment);
  }

  private async _uploadSignature(signature: string): Promise<void> {
    const signaturePath = this._getSignaturePath();
    if (!signaturePath) {
      return;
    }
    await this._fileManager.uploadFile(
      signaturePath,
      decodeFromBase64DataUri(signature)
    );
    await Firestore.patchDoc(this.fundingRequest.ref, {
      status: FundingRequestStatus.Pending,
      data: {
        ...this.fundingRequest.data,
        signaturePath,
      },
    } as Partial<MohFundingRequest>);
  }

  private _getForms(): IMohFormDisplay[] {
    if (isMohClaimFundingRequest(this.fundingRequest)) {
      return MohClaimFundingRequest.formDisplays(this.fundingRequest);
    }
    if (isMohPreApprovalFundingRequest(this.fundingRequest)) {
      return MohPreApprovalFundingRequest.formDisplays(this.fundingRequest);
    }
    return [];
  }

  private _getSignaturePath(): string | undefined {
    if (isMohClaimFundingRequest(this.fundingRequest)) {
      const basePath = this.fundingRequest.data.invoiceRef?.path;
      return basePath ? `${basePath}/signature.png` : undefined;
    }
    if (isMohPreApprovalFundingRequest(this.fundingRequest)) {
      const basePath = this.fundingRequest.data.stepRef?.path;
      return basePath ? `${basePath}/signature.png` : undefined;
    }
  }
}
